import { useInfiniteQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { StaffingService } from '../../../../../js/api';
import { Need as ApiNeed } from '../../../../../js/api/models/Need';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { Button } from '../../../components/button';
import { Drawer, DrawerBody } from '../../../components/drawer';
import { Heading } from '../../../components/heading';
import { ListErrorState } from '../../../components/list-error-state';
import { ListFilter } from '../../../components/list-filter';
import { QueueList } from '../../../components/queue-list';
import StatusPill from '../../../components/StatusPill';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSortHeader,
} from '../../../components/table';
import { WorkflowScopes } from '../../../constants/workflows';
import { useCodeDefinitionsAsMaps } from '../../../hooks/useCodeDefinitions';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useListFilterParams } from '../../../hooks/useListFilterParams';
import { useRequireActingAsEntity } from '../../../hooks/useRequireActingAsEntity';
import { getDateTemporalityClass } from '../../../utils/date';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';
import { STICKY_HEADER } from '../../../utils/globalStyles';
import {
  MAIN_CONTENT_AREA,
  MAIN_CONTENT_AREA_FIXED,
  PAGE_HEADER,
  TABLE_SCROLL_BOX,
} from '../../../utils/layoutStyles';
import { TRANSITION_ALL } from '../../../utils/transitionStyles';
import NeedCard from '../../Organization/Needs/NeedCard';
import { NeedDrawer } from '../../Organization/Needs/NeedDrawer';
import { getUrgencyIcon } from '../../Organization/Needs/utils';

const PAGE_SIZE = 21;
const DATE_FORMAT = 'LLL d, yyyy';

type ApiParams = Parameters<typeof StaffingService.staffingNeedsList>[0];

const CATEGORY_TO_API_PARAM: Record<string, keyof ApiParams> = {
  Specialty: 'specialties',
  UrgencyLevel: 'urgency',
  JustificationType: 'justifications',
};

const CATEGORIES = Object.keys(CATEGORY_TO_API_PARAM) as readonly string[];

export type NeedsListPageProps = {
  title?: string;
  queryKeyBase: string;
  displayTypePrefKey: string;
  showOrganization: boolean;
  queueCategory: string;
  showOrganizationFilter?: boolean;
  linkContext?: 'organization' | 'staff';
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const NeedsListPage: React.FC<NeedsListPageProps> = ({
  title = 'Needs',
  queryKeyBase,
  displayTypePrefKey,
  showOrganization,
  queueCategory,
  showOrganizationFilter = false,
  linkContext = 'organization',
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  useDocumentTitle(title);
  const navigate = useNavigate();
  const canCreateNeed = useHasPermission('create__staffing__need');
  const requireActingAs = useRequireActingAsEntity();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: codeDefinitionMaps } = useCodeDefinitionsAsMaps([
    'Degree',
    'Specialty',
    'ShiftRateTypes',
    'ShiftRateTypeIcons',
    'JustificationType',
    'UrgencyLevel',
  ]);

  const [selectedNeedId, setSelectedNeedId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const selectedQueueCode: string | undefined = searchParams.get('queue') || undefined;

  const { filterValues, updateFilters } = useListFilterParams(searchParams, setSearchParams, {
    categories: CATEGORIES,
    displayTypePrefKey,
  });

  const search = filterValues.search || '';
  const statuses = filterValues.statuses || [];
  const reasons = filterValues.reasons || [];
  const categoryFilters = useMemo(() => filterValues.categories || {}, [filterValues.categories]);
  const healthcareEntity = filterValues.healthcare_entity || '';

  const displayType = (filterValues.displayType || 'card') as 'card' | 'table';
  // Table view: the rows scroll in their own box under a sticky header (utils/layoutStyles).
  const tableScrolls = displayType !== 'card';
  const ordering = (filterValues.ordering || '-updated_at') as string;

  const setOrdering = useCallback(
    (next: string) => {
      updateFilters({
        search: filterValues.search,
        statuses: filterValues.statuses,
        reasons: filterValues.reasons,
        categories: filterValues.categories,
        displayType,
        ordering: next,
        healthcare_entity: filterValues.healthcare_entity,
      });
    },
    [
      updateFilters,
      filterValues.search,
      filterValues.statuses,
      filterValues.reasons,
      filterValues.categories,
      filterValues.healthcare_entity,
      displayType,
    ]
  );

  const apiQueueCode = selectedQueueCode ? (selectedQueueCode as ApiParams['queue']) : undefined;

  const categoryApiParams = useMemo(() => {
    const params: Partial<ApiParams> = {};
    Object.entries(categoryFilters).forEach(([category, values]) => {
      const apiParamName = CATEGORY_TO_API_PARAM[category];
      if (apiParamName && values.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (params as Record<string, unknown>)[apiParamName] = values;
      }
    });
    return params;
  }, [categoryFilters]);

  const queryKey = useMemo(
    () => [
      queryKeyBase,
      selectedQueueCode,
      search,
      statuses,
      reasons,
      categoryFilters,
      ordering,
      healthcareEntity,
    ],
    [
      queryKeyBase,
      selectedQueueCode,
      search,
      statuses,
      reasons,
      categoryFilters,
      ordering,
      healthcareEntity,
    ]
  );

  const { data, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey,
      queryFn: async ({ pageParam = 0 }) => {
        return StaffingService.staffingNeedsList({
          queue: apiQueueCode,
          search: search || undefined,
          status: statuses.length > 0 ? (statuses as Array<string | null>) : undefined,
          reason: reasons.length > 0 ? (reasons as Array<string | null>) : undefined,
          healthcareEntityId: showOrganization ? healthcareEntity || undefined : undefined,
          ...categoryApiParams,
          ordering: ordering as ApiParams['ordering'],
          limit: PAGE_SIZE,
          offset: pageParam as number,
        });
      },
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.next) {
          return allPages.length * PAGE_SIZE;
        }
        return undefined;
      },
    });

  const needs = useMemo(() => data?.pages.flatMap((page) => page.results || []) || [], [data]);
  const totalCount = data?.pages[0]?.count || 0;

  const loadMoreNeeds = React.useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { loadingRef } = useInfiniteScroll(loadMoreNeeds, hasNextPage || false, {
    enabled: hasNextPage && !isLoading,
  });

  useEffect(() => {
    const needIdFromUrl = searchParams.get('id');
    if (needIdFromUrl) {
      setSelectedNeedId(needIdFromUrl);
      setIsDrawerOpen(true);
    } else if (isDrawerOpen) {
      setIsDrawerOpen(false);
      setSelectedNeedId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleNeedCardClick = (need: ApiNeed) => {
    setSelectedNeedId(need.id);
    setIsDrawerOpen(true);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('id', need.id);
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedNeedId(null);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('id');
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleQueueChange = (queueCode: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (queueCode) newSearchParams.set('queue', queueCode);
    else newSearchParams.delete('queue');
    setSearchParams(newSearchParams, { replace: true });
  };

  return (
    <>
      <div className="tw:flex tw:h-[calc(100vh-72px)] tw:overflow-hidden">
        <div
          className={clsx(
            `tw:flex tw:flex-col ${TRANSITION_ALL} tw:overflow-hidden`,
            isDrawerOpen ? 'tw:w-3/5 tw:lg:w-3/5' : 'tw:w-full'
          )}
        >
          <main className={clsx(MAIN_CONTENT_AREA, tableScrolls && MAIN_CONTENT_AREA_FIXED)}>
            <div
              className={clsx(
                STICKY_HEADER,
                tableScrolls && 'tw:flex tw:min-h-0 tw:flex-1 tw:flex-col'
              )}
            >
              <div className={PAGE_HEADER}>
                <Heading level={1} className="tw:!text-brand-green-0">
                  <i className="far fa-fw fa-users-medical tw:mr-1" />
                  {title}
                </Heading>
                {canCreateNeed && (
                  <Button
                    color="brandgreen1"
                    onClick={() => requireActingAs(() => navigate('/app/organization/needs/new'))}
                    className="ms-3"
                  >
                    Create Need
                  </Button>
                )}
              </div>
              <div className="tw:mt-4">
                <QueueList
                  categoryName={queueCategory}
                  notificationScope={WorkflowScopes.Opportunity}
                  selectedQueue={selectedQueueCode || undefined}
                  priorityQueues={true}
                  onQueueChange={handleQueueChange}
                />
              </div>

              <div className="tw:mt-1 tw:group" data-drawer-open={isDrawerOpen ? 'true' : 'false'}>
                <ListFilter
                  show_search={true}
                  workflow_scope={WorkflowScopes.Need}
                  categories={Array.from(CATEGORIES)}
                  showStatus={true}
                  showReason={true}
                  showOrganizationFilter={showOrganizationFilter}
                  showDisplayType={true}
                  labelOverrides={{
                    UrgencyLevel: 'Urgency',
                    JustificationType: 'Justification',
                    Reason: 'Tier/Reason',
                  }}
                  sortOptions={[
                    ...(showOrganization
                      ? [{ label: 'Organization', value: 'healthcare_entity__name' }]
                      : []),
                    { label: 'Last Updated', value: 'updated_at' },
                    { label: 'Created', value: 'created_at' },
                    { label: 'Status Changed On', value: 'status_at' },
                    { label: 'Facility', value: 'facility__name' },
                    { label: 'Location', value: 'facility__city' },
                    { label: 'Status', value: 'current_status__title' },
                    { label: 'Labor Tier', value: 'current_reason__title' },
                    { label: 'Target On', value: 'target_on' },
                    { label: 'Urgency', value: 'urgency_level' },
                    { label: 'Justification', value: 'justification' },
                    { label: '# Unfilled', value: 'num_shifts' },
                    { label: 'Code', value: 'code' },
                  ]}
                  values={filterValues}
                  onFilterChange={updateFilters}
                />
              </div>
              <div className={clsx('tw:mt-3', tableScrolls && TABLE_SCROLL_BOX)}>
                {isLoading && needs.length === 0 && (
                  <div className="tw:text-center tw:py-8">Loading needs...</div>
                )}
                {isError ? (
                  <ListErrorState testId="needs-error" />
                ) : !isLoading && needs.length === 0 ? (
                  <div className="tw-col-span-full tw:text-center tw:text-zinc-500 tw:py-8">
                    No needs found.
                    <div className="tw:text-zinc-500 tw:italic tw:text-sm">
                      Refine your search or create a new need.
                    </div>
                  </div>
                ) : (
                  <>
                    {needs.length > 0 && (
                      <div className="tw:text-right tw:text-xs tw:text-zinc-500 tw:my-1 tw:mr-1">
                        Showing {needs.length} of {totalCount} needs
                      </div>
                    )}

                    {displayType === 'card' ? (
                      <div className="tw:@container">
                        <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:@min-[513px]:grid-cols-2 tw:@min-[793px]:grid-cols-3">
                          {(() => {
                            const needIdFromUrl = searchParams.get('id');
                            return needs.map((need) => {
                              const isSelected = need.id === needIdFromUrl;
                              return (
                                <div
                                  key={need.id}
                                  onClick={() => handleNeedCardClick(need)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleNeedCardClick(need);
                                    }
                                  }}
                                  role="button"
                                  tabIndex={0}
                                >
                                  <NeedCard
                                    need={need}
                                    degreeMap={codeDefinitionMaps?.Degree || {}}
                                    specialtyMap={codeDefinitionMaps?.Specialty || {}}
                                    shiftRateTypeMap={codeDefinitionMaps?.ShiftRateTypes || {}}
                                    shiftRateTypeIconMap={
                                      codeDefinitionMaps?.ShiftRateTypeIcons || {}
                                    }
                                    justificationMap={codeDefinitionMaps?.JustificationType || {}}
                                    urgencyLevelMap={codeDefinitionMaps?.UrgencyLevel || {}}
                                    isSelected={isSelected}
                                  />
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    ) : (
                      <Table striped dense stickyHeader={tableScrolls}>
                        <TableHead>
                          <TableSortHeader
                            sortKey="code"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Code
                          </TableSortHeader>
                          {showOrganization && (
                            <TableSortHeader
                              sortKey="healthcare_entity__name"
                              ordering={ordering}
                              onOrderingChange={setOrdering}
                            >
                              Organization
                            </TableSortHeader>
                          )}
                          <TableSortHeader
                            sortKey="facility__name"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Facility
                          </TableSortHeader>
                          <TableSortHeader
                            sortKey="facility__city"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Location
                          </TableSortHeader>
                          <TableSortHeader
                            sortKey="current_status__title"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Status
                          </TableSortHeader>
                          <TableSortHeader
                            sortKey="current_reason__title"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            {selectedQueueCode === 'active'
                              ? 'Labor Tier'
                              : selectedQueueCode === 'closed'
                                ? 'Reason'
                                : 'Labor Tier/Reason'}
                          </TableSortHeader>
                          <TableSortHeader
                            sortKey="urgency_level"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Urgency
                          </TableSortHeader>
                          <TableSortHeader
                            sortKey="justification"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Justification
                          </TableSortHeader>
                          <TableSortHeader
                            sortKey="num_shifts"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            # Unfilled
                          </TableSortHeader>
                          <TableHeader>Specialty</TableHeader>
                          <TableSortHeader
                            sortKey="target_on"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Target On
                          </TableSortHeader>
                          <TableSortHeader
                            sortKey="updated_at"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Last Updated
                          </TableSortHeader>
                          <TableSortHeader
                            sortKey="created_at"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Created
                          </TableSortHeader>
                        </TableHead>
                        <TableBody>
                          {/* eslint-disable-next-line sonarjs/cognitive-complexity */}
                          {needs.map((need) => {
                            const needIdFromUrl = searchParams.get('id');
                            const isSelected = need.id === needIdFromUrl;
                            return (
                              <TableRow
                                key={need.id}
                                onClick={() => handleNeedCardClick(need)}
                                className={clsx(isSelected ? 'tw:!bg-brand-green-2' : '')}
                              >
                                <TableCell>{need.code || need.id}</TableCell>
                                {showOrganization && (
                                  <TableCell>{need.healthcare_entity?.name || '--'}</TableCell>
                                )}
                                <TableCell>{need.facility?.name || '--'}</TableCell>
                                <TableCell>
                                  {getFacilityLocationDisplayValue(need.facility) || '--'}
                                </TableCell>
                                <TableCell>
                                  <StatusPill
                                    scope={WorkflowScopes.Need}
                                    status={need.current_status}
                                  />
                                </TableCell>
                                <TableCell>{need.current_reason?.title || '--'}</TableCell>
                                <TableCell>
                                  {need.urgency_level ? (
                                    <span className="tw:inline-flex tw:items-center">
                                      <i
                                        className={clsx(
                                          getUrgencyIcon(need.urgency_level || undefined),
                                          'tw:mr-1'
                                        )}
                                      />
                                      {codeDefinitionMaps?.UrgencyLevel[need.urgency_level]}
                                    </span>
                                  ) : (
                                    '--'
                                  )}
                                </TableCell>
                                <TableCell>
                                  {need.justification
                                    ? codeDefinitionMaps?.JustificationType[need.justification]
                                    : '--'}
                                </TableCell>
                                <TableCell>{need.num_shifts}</TableCell>
                                <TableCell>
                                  {need.specialty
                                    ? codeDefinitionMaps?.Specialty[need.specialty]
                                    : '--'}
                                </TableCell>
                                <TableCell>
                                  {need.target_on ? (
                                    <span
                                      className={getDateTemporalityClass(need.target_on, {
                                        warningWithinDays: 90,
                                        criticalUpperLimitDays: 30,
                                      })}
                                    >
                                      {format(parseISO(need.target_on), DATE_FORMAT)}
                                    </span>
                                  ) : (
                                    '--'
                                  )}
                                </TableCell>
                                <TableCell>
                                  {need.updated_at
                                    ? format(parseISO(need.updated_at), DATE_FORMAT)
                                    : '-'}
                                </TableCell>
                                <TableCell>
                                  {need.created_at
                                    ? format(parseISO(need.created_at), DATE_FORMAT)
                                    : '-'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}

                    {hasNextPage && (
                      <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
                        {isFetchingNextPage && (
                          <div className="tw:text-center tw:text-zinc-500">
                            Loading more needs...
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </main>
        </div>

        <Drawer open={isDrawerOpen} onClose={handleCloseDrawer} width="3xl" mode="side-by-side">
          <DrawerBody className="tw:!text-sm/6">
            {selectedNeedId && (
              <NeedDrawer
                key={selectedNeedId}
                needId={selectedNeedId}
                onClose={handleCloseDrawer}
                listQueryKeyBase={queryKeyBase}
                linkContext={linkContext}
              />
            )}
          </DrawerBody>
        </Drawer>
      </div>
    </>
  );
};
